import React from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

interface ProductivityChartsProps {
  data: any;
}

const ProductivityCharts: React.FC<ProductivityChartsProps> = ({ data }) => {
  const {
    hourlyPatterns,
    dailyTrends,
    weeklyPatterns,
    categoryBreakdown,
    priorityAnalysis,
    achievements
  } = data;

  // Colors for charts
  const colors = {
    primary: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    purple: '#8b5cf6',
    pink: '#ec4899',
    cyan: '#06b6d4',
    gray: '#6b7280'
  };

  const pieColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color, fontSize: '14px' }}>
              {entry.name}: {entry.value}
              {entry.name.includes('Rate') || entry.name.includes('Score') ? '%' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Fixed pie chart label function with proper typing
  const renderPieLabel = (entry: any) => {
    const percent = entry.percent || 0;
    const name = entry.name || 'Unknown';
    return `${name}: ${(percent * 100).toFixed(0)}%`;
  };

  return (
    <div className="space-y-6">
      
      {/* Hourly Productivity Pattern */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
          ⏰ Hourly Productivity Pattern
        </h3>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
          Find your peak performance hours - when you're most productive throughout the day
        </p>
        
        <div style={{ height: '300px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={hourlyPatterns.hourlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis 
                dataKey="timeLabel" 
                fontSize={12}
                tick={{ fill: '#6b7280' }}
              />
              <YAxis fontSize={12} tick={{ fill: '#6b7280' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="productivityScore" 
                name="Productivity Score"
                fill={colors.primary}
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="totalTasks" 
                name="Total Tasks"
                fill={colors.success}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div style={{ 
          marginTop: '1rem', 
          padding: '12px', 
          backgroundColor: '#f0f9ff', 
          borderRadius: '8px',
          fontSize: '0.875rem',
          color: '#0369a1'
        }}>
          🌟 <strong>Peak Hour:</strong> {hourlyPatterns.peakHourLabel} with {hourlyPatterns.peakProductivity}% productivity
        </div>
      </div>

      {/* Daily Trends */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
          📈 Daily Productivity Trends
        </h3>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
          Track your productivity patterns and completion rates over time
        </p>
        
        <div style={{ height: '300px' }}>
          {dailyTrends && dailyTrends.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyTrends}>
                <defs>
                  <linearGradient id="colorCompletion" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors.success} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={colors.success} stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="colorProductivity" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors.primary} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={colors.primary} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis 
                  dataKey="dayName" 
                  fontSize={12}
                  tick={{ fill: '#6b7280' }}
                />
                <YAxis fontSize={12} tick={{ fill: '#6b7280' }} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="completionRate"
                  name="Completion Rate"
                  stroke={colors.success}
                  fillOpacity={1}
                  fill="url(#colorCompletion)"
                />
                <Area
                  type="monotone"
                  dataKey="productivityScore"
                  name="Productivity Score"
                  stroke={colors.primary}
                  fillOpacity={1}
                  fill="url(#colorProductivity)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
                <p>No data available for this period</p>
                <p style={{ fontSize: '0.75rem' }}>Complete some tasks to see your trends!</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Weekly Patterns & Category Breakdown - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Weekly Patterns */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            📅 Weekly Patterns
          </h3>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
            Which days of the week are you most productive?
          </p>
          
          <div style={{ height: '250px' }}>
            {weeklyPatterns && weeklyPatterns.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklyPatterns} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis type="number" fontSize={12} tick={{ fill: '#6b7280' }} domain={[0, 100]} />
                  <YAxis 
                    type="category" 
                    dataKey="dayName" 
                    fontSize={12}
                    tick={{ fill: '#6b7280' }}
                    width={50}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="productivityScore" 
                    name="Productivity Score"
                    fill={colors.purple}
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📅</div>
                  <p>No weekly data yet</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            🎯 Category Breakdown
          </h3>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
            How you distribute your time across different task categories
          </p>
          
          <div style={{ height: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderPieLabel}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="totalTasks"
                  fontSize={12}
                >
                  {categoryBreakdown.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: any, name: any) => [value, 'Tasks']}
                  labelFormatter={(label: any) => `Category: ${label}`}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Priority Analysis & Achievements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Priority Analysis */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            ⚡ Priority Analysis
          </h3>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
            How well do you handle tasks by priority level?
          </p>
          
          <div style={{ height: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={priorityAnalysis}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis 
                  dataKey="name" 
                  fontSize={12}
                  tick={{ fill: '#6b7280' }}
                />
                <YAxis fontSize={12} tick={{ fill: '#6b7280' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  dataKey="completionRate" 
                  name="Completion Rate"
                  fill={colors.warning}
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  dataKey="totalTasks" 
                  name="Total Tasks"
                  fill={colors.cyan}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Achievements */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            🏆 Achievements Unlocked
          </h3>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
            Celebrate your productivity milestones!
          </p>
          
          {achievements.length > 0 ? (
            <div className="space-y-3">
              {achievements.map((achievement: any, index: number) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '8px'
                  }}
                >
                  <div style={{ fontSize: '1.5rem', marginRight: '12px' }}>
                    {achievement.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {achievement.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {achievement.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
              <p className="text-gray-500 text-sm">
                Complete more tasks to unlock achievements!
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default ProductivityCharts;
